import {
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { userService } from "@/app/api/user";
import { useAuthedQuery } from "@/hooks/queries/use-authed-query.hook";
import {
  type UserListUser,
  type UserManagementListUser,
  UserRole,
} from "@/types/user";

const USER_QUERY_KEY_BASE = "user";
export const USER_QUERY_KEYS = {
  base: [USER_QUERY_KEY_BASE] as const,
  userManagement: [USER_QUERY_KEY_BASE, "userManagement"],
};

export const useUsersQuery = (
  config: Partial<UseQueryOptions<UserListUser[]>> = {},
) => {
  return useAuthedQuery({
    queryFn: () => userService.getUsers(),
    queryKey: USER_QUERY_KEYS.base,
    ...config,
  });
};

export const useUsersManagementListQuery = (
  config: Partial<UseQueryOptions<UserManagementListUser[]>> = {},
) => {
  return useAuthedQuery({
    queryFn: () => userService.getUsersManagementList(),
    queryKey: USER_QUERY_KEYS.userManagement,
    ...config,
  });
};

export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string; fullName?: string; role?: UserRole }) =>
      userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userManagement,
      });
    },
  });
};

export const useUpdateUserRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      userService.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userManagement,
      });
    },
  });
};

export const useDeactivateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userService.deactivateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userManagement,
      });
    },
  });
};

export const useUpdateCanSendPdfsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      canSendCandidatePdfs,
    }: {
      userId: string;
      canSendCandidatePdfs: boolean;
    }) => userService.updateCanSendCandidatePdfs(userId, canSendCandidatePdfs),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userManagement,
      });
    },
  });
};

export const useReInviteUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userService.reInviteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userManagement,
      });
    },
  });
};
